import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateOAuthClient,
  AdminFetchOAuthClient,
  AdminRevokeOAuthTokens,
  AdminUpdateOAuthClient,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type { OAuthScope } from 'src/constants/oauth/enums'
import type {
  CreateOAuthClientPayload,
  OAuthClient,
  UpdateOAuthClientPayload,
} from 'src/interfaces/oauth'
import { parseOAuthClientResponse } from 'src/utils/oauthClient'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

interface PartnerScopedPayload {
  partnerId?: string
}

const partnerIdParams = (partnerId: string) => {
  return [{ field: ':partnerId', value: partnerId }]
}

/**
 * The create response is the only source of a client secret in the whole system — the DB keeps
 * a sha256 hash and there is no rotate endpoint. So a response we cannot read must throw rather
 * than dispatch a success: the admin would otherwise be shown an empty secret panel for a
 * client they can never re-create.
 */
function assertCreatedOAuthClient(payload: unknown): {
  client: OAuthClient
  clientSecret: string
} {
  const client = parseOAuthClientResponse(payload)
  const clientSecret = (payload as { clientSecret?: unknown } | null)?.clientSecret

  if (client === null || typeof clientSecret !== 'string' || clientSecret === '') {
    throw new Error('OAuth client create response is missing a client secret')
  }

  return { client, clientSecret }
}

function assertUpdatedOAuthClient(payload: unknown): OAuthClient {
  const client = parseOAuthClientResponse(payload)

  if (client === null) {
    throw new Error('OAuth client update response is not a client')
  }

  return client
}

export function* onFetchOAuthClient(action: AsyncAction<PartnerScopedPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchOAuthClient.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    let client: OAuthClient | null = null

    try {
      const { payload }: { payload: unknown } = yield call(
        callSecureApi,
        Endpoints.AdminFetchOAuthClient,
        {
          method: 'GET',
          params: partnerIdParams(partnerId),
        },
      )

      client = parseOAuthClientResponse(payload)
    } catch (error: unknown) {
      // A partner with no OAuth client is the common case and answers 404 — an expected result,
      // not a failure, so it resolves as `client: null` and is deliberately never sent to
      // Sentry. The backend uses the same 404 for 'Partner not found', which this tab cannot
      // distinguish. That is safe here: a nonexistent customerId is not reachable through
      // normal navigation, and if it were, the create form's POST would surface the backend's
      // own 'Partner not found' message.
      if ((error as { status?: number } | null)?.status !== 404) {
        throw error
      }
    }

    yield put(AdminFetchOAuthClient.success({ partnerId, client }))

    if (action.next) {
      action.next(null, client)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.oauthClient.onFetchOAuthClient] error' })

    yield put(AdminFetchOAuthClient.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load OAuth client'))
    }
  }
}

export function* onCreateOAuthClient(action: AsyncAction<CreateOAuthClientPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminCreateOAuthClient.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // The body is built explicitly rather than forwarded so `partnerId` — which belongs in the
    // path — never reaches the body, where the DTO would reject it.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminCreateOAuthClient,
      {
        method: 'POST',
        params: partnerIdParams(partnerId),
        body: { scopes: action.payload.scopes },
      },
    )

    const { client, clientSecret } = assertCreatedOAuthClient(payload)

    // The secret must never become an action payload or a state field: `redux-logger` prints
    // every action and the whole store to the console in all environments, AppLogger's redux
    // middleware forwards them, and Sentry keeps actions as breadcrumbs plus a full state
    // attachment. The success action therefore carries only the client; the secret reaches the
    // page as the resolved value of `action.next`, which never enters the store.
    yield put(AdminCreateOAuthClient.success({ partnerId, client }))

    if (action.next) {
      action.next(null, { partnerId, client, clientSecret })
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.oauthClient.onCreateOAuthClient] error' })

    yield put(AdminCreateOAuthClient.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create the OAuth client'))
    }
  }
}

export function* onUpdateOAuthClient(action: AsyncAction<UpdateOAuthClientPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminUpdateOAuthClient.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // `deactivate` is included only when the caller passed it. The backend leaves
    // `deactivatedAt` untouched when the key is absent, so omitting it is exactly what keeps a
    // scopes-only save from turning API access on or off.
    const body: { scopes: OAuthScope[]; deactivate?: boolean } = {
      scopes: action.payload.scopes,
    }

    if (action.payload.deactivate !== undefined) {
      body.deactivate = action.payload.deactivate
    }

    yield call(callSecureApi, Endpoints.AdminUpdateOAuthClient, {
      method: 'PUT',
      params: partnerIdParams(partnerId),
      body,
    })

    // The PUT response never carries `clientId` (only the fields the caller changed), so it can
    // never parse as a client. Re-fetch by GET for the authoritative post-update shape instead of
    // treating that as a failed update.
    const { payload: fetchedPayload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchOAuthClient,
      {
        method: 'GET',
        params: partnerIdParams(partnerId),
      },
    )

    const client = assertUpdatedOAuthClient(fetchedPayload)

    yield put(AdminUpdateOAuthClient.success({ partnerId, client }))

    if (action.next) {
      action.next(null, client)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.oauthClient.onUpdateOAuthClient] error' })

    yield put(AdminUpdateOAuthClient.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update the OAuth client'))
    }
  }
}

export function* onRevokeOAuthTokens(action: AsyncAction<PartnerScopedPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminRevokeOAuthTokens.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminRevokeOAuthTokens,
      {
        method: 'POST',
        params: partnerIdParams(partnerId),
      },
    )

    const revokedCountValue = (payload as { revokedCount?: unknown } | null)?.revokedCount
    // The count is reported to the admin, not acted on, so an unreadable one falls back to 0
    // rather than failing a revocation the backend already performed.
    const revokedCount = typeof revokedCountValue === 'number' ? revokedCountValue : 0
    const result = { revokedCount }

    yield put(AdminRevokeOAuthTokens.success(result))

    if (action.next) {
      action.next(null, result)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.oauthClient.onRevokeOAuthTokens] error' })

    yield put(AdminRevokeOAuthTokens.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to revoke the OAuth tokens'))
    }
  }
}

export function* oauthClientSaga() {
  // Tab and customer changes re-trigger the fetch, so only the newest run matters.
  yield takeLatest(AdminFetchOAuthClient.type.REQUEST, onFetchOAuthClient)
  // Mutations must each run.
  yield takeEvery(AdminCreateOAuthClient.type.REQUEST, onCreateOAuthClient)
  yield takeEvery(AdminUpdateOAuthClient.type.REQUEST, onUpdateOAuthClient)
  yield takeEvery(AdminRevokeOAuthTokens.type.REQUEST, onRevokeOAuthTokens)
}
